import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import Spinner from 'react-spinkit';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { dateFormats } from '../../../../constants';
import { adminActions } from '../../../../state/modules/admin';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream } from '../../../../utils';
import { DateSelect, Icon } from '../../../components';

const ContentAdminDashboard = ({
  exportContent,
  filter,
  onDateRangeChange,
  isFetchingReport,
}) => (
    <div className='base-layout'>
      <section className='layout--header'>
        <Grid>
          <Row className='button-gutter'>
            <Col xs={12}>
              <h1>Content admin</h1>
            </Col>
          </Row>
        </Grid>
      </section>
      <section id='section--admin-content' className='layout--section'>
        <Grid>
          <Row className='no-gutter sectionHeader'>
            <form>
              <table className='table--date-search'>
                <tbody>
                  <tr>
                    <td>
                      Export content between
                    </td>
                    <td>
                      <DateSelect date={filter.fromDate} onChange={(date) => onDateRangeChange('fromDate', date)} />
                    </td>
                    <td>
                      and
                    </td>
                    <td>
                      <DateSelect date={filter.toDate} onChange={(date) => onDateRangeChange('toDate', date)} futureYears={1} />
                    </td>
                    <td style={{ width: '250px'}}>
                      <Button
                        className='large full pull-down'
                        onClick={exportContent}
                        style={{ width: '100%'}}
                      >
                        {
                          isFetchingReport ? 
                            <Spinner name='circle' className='spinner' fadeIn='none' /> :
                            <div><Icon iconSet='glyphicon' icon='download-alt' />export to csv</div>
                        }
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </Row>
        </Grid>
      </section>
    </div>
  )

const contentReportMap = {
  contentId: {
    title: 'Content Id',
    transform: ({ contentId }) => contentId
  },
  title: {
    title: 'Title',
    transform: ({ title }) => he.decode(title)
  },
  cpdCategory: {
    title: 'Area Of Advice',
    transform: ({ cpdCategory }) => he.decode(cpdCategory)
  },
  industry: {
    title: 'Industry',
    transform: ({ industry }) => he.decode(industry)
  },
  contentType: {
    title: 'Content Type',
    transform: ({ contentType }) => he.decode(contentType)
  },
  publisher: {
    title: 'Publisher',
    transform: ({ publisher }) => he.decode(publisher)
  },
  status: {
    title: 'Status',
    transform: ({ status }) => he.decode(status)
  },
  expiryDate: {
    title: 'Expiry Date',
    transform: ({ expiryDate }) => dateFnsCompat.format(expiryDate, dateFormats.ShortDate)
  },
  cpdPoints: {
    title: 'CPD Points',
    transform: ({ cpdPoints }) => cpdPoints
  },
  numOfAttempts: {
    title: 'Number of Attempts',
    transform: ({ numOfAttempts }) => numOfAttempts
  },
  numOfCompletion: {
    title: 'Number of Completion',
    transform: ({ numOfCompletion }) => numOfCompletion
  },
  dateViewed: {
    title: 'Date Viewed',
    transform: ({ dateViewed }) => dateViewed ? dateFnsCompat.format(dateViewed, dateFormats.ShortDate) : ''
  }
}

const mapStateToProps = ({ admin, industry }) => ({
  contentReport: admin.contentReport,
  allGoals: fp.flow(
    fp.flatMap('goals'),
    fp.uniqBy('id')
  )(industry.industriesWithCpdCategorys),
  isFetchingReport: admin.isFetchingReport
})

const mapDispatchToProps = (dispatch) => ({
  getContentReport: bindActionCreators(adminActions.admin.getContentReport, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(() => ({
    filter: {
      fromDate: dateFnsCompat.format(dateFnsCompat.subDays(new Date(), 6)),
      toDate: dateFnsCompat.format(dateFnsCompat.subDays(dateFnsCompat.endOfDay(new Date()), 0))
    }
  }),
    {
      onDateRangeChange: ({ filter }) => (key, date) => ({
        filter: {
          ...filter,
          [key]: key === 'toDate' ? dateFnsCompat.format(dateFnsCompat.endOfDay(date)) : dateFnsCompat.format(date)
        }
      })
    }
  ),
  withProps(
    ({ contentReport, allGoals }) => ({
      columns: fp.flow(
        fp.keys,
        fp.map(
          (key) => fp.flow(
            fp.get(key),
            fp.get('title')
          )(contentReportMap)),
        (contentColumns) => {
          const goalColumns = fp.map('name')(allGoals)
          return contentColumns.concat(goalColumns)
        }
      )(contentReportMap),
      rows: fp.map(
        (row) =>
          fp.flow(
            fp.keys,
            fp.map(
              (key) => fp.flow(
                fp.get(key),
                fp.get('transform')
              )(contentReportMap)(row)
            ),
            (contentData) => {
              const goalData = allGoals.map(
                ({ id }) => fp.includes(id)(row.goals) ? row.cpdPoints : 0
              )
              return contentData.concat(goalData)
            }
          )(contentReportMap)
      )(contentReport)
    })
  ),
  withHandlers({
    exportContent: ({
      filter,
      getContentReport
    }) => (e) => {
      getContentReport(filter)
    },
    exportCsv: ({ columns, rows }) => () => {
      downloadBlobStreamFile(writeDataToCsvStream(getCsvStream({
        header: true,
        columns
      }), rows).pipe(getBlobStream()), 'content-report.csv')
    }
  }),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      const { isFetchingReport: wasFetchingReport } = this.props
      const { isFetchingReport, exportCsv } = nextProps
      if (wasFetchingReport && !isFetchingReport) {
        exportCsv()
      }
    }
  })
)(ContentAdminDashboard)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/content-portal/content.js